// Libs
import { ErrorMessage, type FieldHelperProps, useField } from "formik";
import { capitalize, uniq } from "lodash";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

// Types
import type { IdName } from "src/types/IdName";

// Utils
import { useTranslation } from "src/design-system/core/TranslationContext";
import { findSmallestDate } from "src/utils/dateUtils";
import { FORMAT_DATE_STD, formatHoursPlus24, formatTimeSlot } from "src/utils/formatters";
import { buildHoursSlot, formatSlotToTime } from "src/utils/hourSlots";
import {
    resetFieldsFonction,
    type SetModalResetFieldDisplay,
} from "src/views/marketing-actions/marketing-actions-create-view/MarketingAction.utils";
import { useRecurrenceCardStyles } from "./RecurrenceCard.styles";

// Components
import { DatePicker } from "@mui/lab";
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    createFilterOptions,
    FormControlLabel,
    FormHelperText,
    Tooltip,
} from "@mui/material";
import { WeekDayCheckBox } from "src/components/Selector/WeekDayCheckBox";
import type { PartialRecord } from "src/utils/types";
import { MarketingActionVariantEnum } from "src/utils/variants";
import {
    MARKETING_ACTIONS_CREATE_VIEW_TK,
    MARKETING_ACTIONS_TRANSLATION_NAMESPACE,
} from "src/views/marketing-actions/config";
import { CardTitle } from "../CardTitle";
import { DatePickerComponent } from "../DatePicker";
import { MultipleAutocompleteComponent } from "../MultipleAutocomplete";

interface OptionType {
    inputValue?: string;
    name?: string;
    id?: number | string;
}
const filter = createFilterOptions<OptionType>();

interface RecurrenceCardComponentCardProps {
    setModalResetFieldDisplay: SetModalResetFieldDisplay;
    title: string;
    value?: IdName[];
    defaultDisabledField: string[];
    OptionalInformation?: React.ReactNode;
    isSpecialOperation?: boolean;
    specialOperationValues?: any;
    variants: MarketingActionVariantEnum[];
    isForeverDisabled: boolean;
}

export enum RecurrenceFrequence {
    ONETIME = "ONETIME",
    ALLDAY = "ALLDAY",
    WEEKLYDAYS = "WEEKLYDAYS",
    SAMEDAYWEEKLY = "SAMEDAYWEEKLY",
    SPE = "SPE",
}

const DISPLAY_RECURRENCE_FREQUENCE: RecurrenceFrequence[] = [
    RecurrenceFrequence.ONETIME,
    RecurrenceFrequence.ALLDAY,
    RecurrenceFrequence.WEEKLYDAYS,
    RecurrenceFrequence.SPE,
];

export const WEEKDAYS_PER_FREQUENCE: PartialRecord<RecurrenceFrequence, number[] | undefined> = {
    [RecurrenceFrequence.ONETIME]: [],
    [RecurrenceFrequence.ALLDAY]: [1, 2, 3, 4, 5, 6, 0],
    [RecurrenceFrequence.WEEKLYDAYS]: [1, 2, 3, 4, 5],
    [RecurrenceFrequence.SPE]: [1, 2, 3, 4, 5, 6, 0],
};

export function RecurrenceCard({
    title,
    setModalResetFieldDisplay,
    defaultDisabledField,
    OptionalInformation,
    isSpecialOperation,
    specialOperationValues,
    variants,
    isForeverDisabled,
}: RecurrenceCardComponentCardProps): JSX.Element {
    const [, specificValuesMeta] = useField("specificValues");
    const [disabledFields, setDisabledFields] = useState(defaultDisabledField);
    const [field, meta, helpers] = useField("recurrence");

    const [, , helpersStartTime] = useField("recurrence.startTime");
    const [, , helpersEndTime] = useField("recurrence.endTime");

    const classes = useRecurrenceCardStyles();
    const isSmartScheduler = variants.includes(MarketingActionVariantEnum.SmartScheduler);
    const possTimezones = meta.value.possTimezone;
    const smallestDate = findSmallestDate(possTimezones);

    const { t, translateCommon } = useTranslation(
        `${MARKETING_ACTIONS_TRANSLATION_NAMESPACE}:${MARKETING_ACTIONS_CREATE_VIEW_TK}`
    );

    const timeOptions = useMemo(() => {
        return buildHoursSlot(translateCommon).map((hour) => hour);
    }, []);

    useEffect(() => {
        if (isSpecialOperation === false) {
            return;
        }
        setDisabledFields(
            uniq([
                ...disabledFields.filter(
                    (e) =>
                        ![
                            "recurrence.startDate",
                            "recurrence.startTime",
                            "recurrence.frequence",
                        ].includes(e)
                ),
            ])
        );
    }, [isSpecialOperation]);

    const authorizeSmartSchedulerNow = import.meta.env.VITE_AUTHORIZE_SMART_SCHEDULER_NOW
        ? import.meta.env.VITE_AUTHORIZE_SMART_SCHEDULER_NOW === "true"
        : false;

    const filterOptionsFn = (options, params) => {
        const filtered = filter(options, params);

        const timeRegex = /^[0-9]{1,2}:[0-9]{2}$/i;

        if (
            (!isSmartScheduler || authorizeSmartSchedulerNow) &&
            filtered.length === 0 &&
            params.inputValue !== "" &&
            timeRegex.test(params.inputValue)
        ) {
            const [hoursStr, minutesStr] = params.inputValue.split(":");
            const hours = Number.parseInt(hoursStr, 10);
            const minutes = Number.parseInt(minutesStr, 10);
            const currentDate = moment();
            const formattedCurrentDate = currentDate.format("LT");

            if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
                if (formattedCurrentDate === currentDate.format("h:mm A")) {
                    const formattedHours = formatHoursPlus24(hours);
                    const firstDozenTimeSlot = formatTimeSlot(
                        `${formattedHours}:${minutes}`,
                        translateCommon
                    );
                    const secondDozenTimeSlot = formatTimeSlot(
                        `${hours + 12}:${minutes}`,
                        translateCommon
                    );

                    if (formattedHours >= 24) {
                        filtered.push({
                            inputValue: secondDozenTimeSlot,
                            name: `${translateCommon("actions.add")} ${secondDozenTimeSlot}`,
                        });
                        filtered.push({
                            inputValue: firstDozenTimeSlot,
                            name: `${translateCommon("actions.add")} ${firstDozenTimeSlot}`,
                        });
                    } else {
                        filtered.push({
                            inputValue: firstDozenTimeSlot,
                            name: `${translateCommon("actions.add")} ${firstDozenTimeSlot}`,
                        });
                        filtered.push({
                            inputValue: secondDozenTimeSlot,
                            name: `${translateCommon("actions.add")} ${secondDozenTimeSlot}`,
                        });
                    }
                } else {
                    const formattedHours = formatHoursPlus24(hours);
                    const time = formatTimeSlot(`${formattedHours}:${minutes}`, translateCommon);

                    filtered.push({
                        inputValue: time,
                        name: `${translateCommon("actions.add")} ${time}`,
                    });
                }
            }
        }

        return filtered;
    };

    function timeProps(helpers: FieldHelperProps<any>) {
        return variants.includes(MarketingActionVariantEnum.AutoRule)
            ? {
                  isHaveToSelfChange: false,
                  handleChange: (newValue: number & { id: number }) => {
                      setModalResetFieldDisplay(
                          {
                              functionToConfirm: () => {
                                  if (newValue?.id) {
                                      helpers.setValue(newValue?.id);
                                  } else {
                                      helpers.setValue(newValue);
                                  }
                              },
                          },
                          specificValuesMeta
                      );
                  },
              }
            : {};
    }
    return (
        <Card>
            <CardContent>
                <CardTitle>{title}</CardTitle>
                <Box px={8} mt={3} mb={1}>
                    <Box display="flex" flexDirection="column" style={{ gap: "24px" }}>
                        <MultipleAutocompleteComponent
                            multiple={false}
                            inputProps={{
                                label: t("steps.recurrence.title"),
                            }}
                            name={"recurrence.frequence"}
                            disabled={disabledFields.includes("recurrence.frequence")}
                            options={DISPLAY_RECURRENCE_FREQUENCE.map((elm) => {
                                return {
                                    id: elm,
                                    name: t(elm),
                                };
                            })}
                            handleChange={(v) => {
                                resetFieldsFonction(
                                    ["days", "frequence", "endDate"],
                                    { field, meta, helpers },
                                    {
                                        frequence: v.id,
                                        days: WEEKDAYS_PER_FREQUENCE[v.id],
                                        endDate:
                                            v.id === RecurrenceFrequence.ONETIME
                                                ? null
                                                : field.value.endDate,
                                        ending:
                                            v.id === RecurrenceFrequence.ONETIME
                                                ? "date"
                                                : field.value.ending,
                                    }
                                );
                            }}
                        />

                        {field.value && field.value.frequence !== RecurrenceFrequence.ONETIME && (
                            <Box>
                                <WeekDayCheckBox
                                    value={field.value.days}
                                    handleChange={(v) => {
                                        resetFieldsFonction(
                                            ["days", "frequence"],
                                            { field, meta, helpers },
                                            { days: v, frequence: RecurrenceFrequence.SPE }
                                        );
                                    }}
                                />
                                <ErrorMessage name={"recurrence.days"}>
                                    {(message) => <FormHelperText error>{message}</FormHelperText>}
                                </ErrorMessage>
                            </Box>
                        )}

                        <DatePicker
                            label="Controlled picker"
                            value={field.value.startDate}
                            onChange={(newValue) => {
                                helpers.setValue(newValue);
                            }}
                        />
                        <DatePickerComponent
                            name={"recurrence.startDate"}
                            label={t(
                                field.value.frequence === RecurrenceFrequence.ONETIME
                                    ? "steps.recurrence.date"
                                    : "steps.recurrence.start-date"
                            )}
                            disabled={disabledFields.includes("recurrence.startDate")}
                            minDate={
                                isSpecialOperation && specialOperationValues
                                    ? moment
                                          .max(
                                              moment(
                                                  specialOperationValues.specialOperationStartDate
                                              ),
                                              moment(smallestDate, FORMAT_DATE_STD)
                                          )
                                          .toDate()
                                    : moment(smallestDate, FORMAT_DATE_STD).toDate()
                            }
                            maxDate={
                                isSpecialOperation
                                    ? specialOperationValues?.specialOperationEndDate
                                    : null
                            }
                        />

                        {field.value && field.value.frequence !== RecurrenceFrequence.ONETIME && (
                            <Box
                                display="flex"
                                alignItems="center"
                                style={{ gap: "16px" }}
                                flex="1"
                            >
                                <Box display="flex" flexDirection="column" flex={1}>
                                    <Tooltip
                                        title={
                                            field.value.ending === "forever"
                                                ? t("steps.recurrence.end-date-tooltip")
                                                : ""
                                        }
                                    >
                                        <Box display="flex" flexDirection="column" flex={1}>
                                            <DatePickerComponent
                                                style={{ flex: "1" }}
                                                name={"recurrence.endDate"}
                                                label={t("steps.recurrence.end-date")}
                                                disabled={
                                                    disabledFields.includes("recurrence.endDate") &&
                                                    !isSpecialOperation
                                                }
                                                minDate={
                                                    isSpecialOperation && specialOperationValues
                                                        ? moment
                                                              .max(
                                                                  moment(
                                                                      specialOperationValues.specialOperationStartDate
                                                                  ),
                                                                  moment(
                                                                      smallestDate,
                                                                      FORMAT_DATE_STD
                                                                  )
                                                              )
                                                              .toDate()
                                                        : moment(
                                                              smallestDate,
                                                              FORMAT_DATE_STD
                                                          ).toDate()
                                                }
                                                maxDate={
                                                    isSpecialOperation
                                                        ? specialOperationValues?.specialOperationEndDate
                                                        : null
                                                }
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>

                                <div className={classes.hr} />

                                <FormControlLabel
                                    label={capitalize(t("non-stop"))}
                                    control={
                                        <Tooltip
                                            title={
                                                isForeverDisabled
                                                    ? t(
                                                          "steps.recurrence.disabled-forever-checkbox-tooltip"
                                                      )
                                                    : ""
                                            }
                                        >
                                            <Box>
                                                <Checkbox
                                                    name={"ending"}
                                                    disabled={
                                                        isSpecialOperation || isForeverDisabled
                                                    }
                                                    checked={field.value.ending === "forever"}
                                                    onChange={(event) => {
                                                        const v = event.target.checked
                                                            ? "forever"
                                                            : "date";
                                                        resetFieldsFonction(
                                                            ["endDate", "ending"],
                                                            { field, meta, helpers },
                                                            { endDate: null, ending: v }
                                                        );
                                                        if (v === "forever") {
                                                            setDisabledFields(
                                                                uniq([
                                                                    ...disabledFields,
                                                                    "recurrence.endDate",
                                                                ])
                                                            );
                                                        } else {
                                                            setDisabledFields(
                                                                uniq([
                                                                    ...disabledFields.filter(
                                                                        (e) =>
                                                                            e !==
                                                                            "recurrence.endDate"
                                                                    ),
                                                                ])
                                                            );
                                                        }
                                                    }}
                                                />
                                            </Box>
                                        </Tooltip>
                                    }
                                />
                            </Box>
                        )}
                        <Box>
                            <Box display="flex" style={{ gap: "24px" }}>
                                <MultipleAutocompleteComponent
                                    fullWidth
                                    multiple={false}
                                    inputProps={{
                                        label: t("steps.recurrence.start-time"),
                                    }}
                                    {...timeProps(helpersStartTime)}
                                    defaultDisplayValue={12}
                                    name={"recurrence.startTime"}
                                    disabled={disabledFields.includes("recurrence.startTime")}
                                    filterOptions={filterOptionsFn}
                                    options={[
                                        ...(field.value.startTime &&
                                        timeOptions.findIndex(
                                            (to) => to.id === field.value.startTime
                                        ) === -1
                                            ? [
                                                  {
                                                      id: field.value.startTime,
                                                      name: formatSlotToTime(
                                                          field.value.startTime,
                                                          translateCommon
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        ...timeOptions,
                                    ].sort((a, b) => {
                                        return a.id - b.id;
                                    })}
                                />

                                <MultipleAutocompleteComponent
                                    fullWidth
                                    multiple={false}
                                    inputProps={{
                                        label: t("steps.recurrence.end-time"),
                                    }}
                                    defaultDisplayValue={
                                        field.value.startTime
                                            ? // Arrondi pour éviter les problèmes de précision qu'il peut y avoir avec Javascript (par exemple 15.44+2)
                                              Math.round(
                                                  Math.min(field.value.startTime + 2, 28.59) * 100
                                              ) / 100
                                            : 12
                                    }
                                    name={"recurrence.endTime"}
                                    {...timeProps(helpersEndTime)}
                                    filterOptions={filterOptionsFn}
                                    options={[
                                        ...(field.value.endTime &&
                                        timeOptions.findIndex(
                                            (to) => to.id === field.value.endTime
                                        ) === -1
                                            ? [
                                                  {
                                                      id: field.value.endTime,
                                                      name: formatSlotToTime(
                                                          field.value.endTime,
                                                          translateCommon
                                                      ),
                                                  },
                                              ]
                                            : []),
                                        ...timeOptions,
                                    ].sort((a, b) => {
                                        return a.id - b.id;
                                    })}
                                />
                            </Box>
                            <Box className="italic-text" style={{ fontSize: 10 }} pt={1}>
                                {t("steps.recurrence.same-hour-every-day")}
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {OptionalInformation && <Box padding={2}>{OptionalInformation}</Box>}
            </CardContent>
        </Card>
    );
}
